/**
 * ProductUpsell Web Component
 * Custom element for handling product upsell functionality with Rebuy integration
 */
class ProductUpsell extends HTMLElement {
  constructor() {
    super();
    this.items = [];
    this.cachedItems = [];
    this.accessToken = '7b2cc70a93fab60e26b8b86ef5e82977';
    this.isCustomizer = false;
    this.boundHandleRebuyEvent = this.handleRebuyEvent.bind(this);
  }


  connectedCallback() {
    this.blockId = this.getAttribute('data-block-id');
    this.widgetId = this.getAttribute('data-rebuy-widget-id');
    this.cachedItems = window[`cachedUpsellItems_${this.blockId}`] || [];
    this.isCustomizer = window.Shopify?.designMode || false;

    // Find child elements
    this.container = this.querySelector(`#frequently_purchased_container_${this.blockId}`);
    this.productsContainer = this.querySelector(`#frequently_purchased_products_${this.blockId}`);
    this.skeleton = this.querySelector(`#skeleton_${this.blockId}`);

    this.init();
  }

  disconnectedCallback() {
    // Clean up event listeners
    document.removeEventListener('rebuy.ready', this.boundHandleRebuyEvent);
  }

  init() {
    if (this.skeleton) this.skeleton.style.display = 'block';
    if (this.container) this.container.style.display = 'none';

    // If in customizer, use fallback data
    if (this.isCustomizer) {
      this.loadFallbackData();
      return;
    }

    // Normal Rebuy initialization for frontend
    document.removeEventListener('rebuy.ready', this.boundHandleRebuyEvent);
    document.addEventListener('rebuy.ready', this.boundHandleRebuyEvent);

    if (window.Rebuy?.widgets?.length > 0) {
      const existingWidget = window.Rebuy.widgets.find(w => w.id === this.widgetId);
      if (existingWidget?.data?.products) {
        this.handleRebuyEvent({ widget: existingWidget });
      }
    }

    if (window.Rebuy?.init) {
      setTimeout(() => window.Rebuy.init(), 0);
    }
  }

  async loadFallbackData() {
    try {
      // Get product ID from the data attribute or URL
      const productId = this.getAttribute('data-product-id') || this.extractProductIdFromUrl();

      if (!productId) {
        this.loadStaticFallbackData();
        return;
      }

      // Fetch current product recommendations
      const response = await fetch(`${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=3`);
      const data = await response.json();

      if (data.products && data.products.length > 0) {
        this.items = data.products.map(product => ({
          id: product.id,
          image: product.featured_image,
          title: product.title,
          price: (product.price / 100).toFixed(2),
          comparePrice: product.compare_at_price ? (product.compare_at_price / 100).toFixed(2) : null,
          description: product.description ? this.trimDescription(product.description) : '',
          url: product.url,
          nonClickable: this.hasNonClickableTag(product.tags)
        }));

        this.render();
      } else {
        // If no recommendations, use fallback static data
        this.loadStaticFallbackData();
      }
    } catch (error) {
      console.error('Error loading fallback data:', error);
      this.loadStaticFallbackData();
    }
  }

  extractProductIdFromUrl() {
    // Try to extract product ID from current URL or meta tags
    const productMeta = document.querySelector('meta[name="product-id"]');
    if (productMeta) return productMeta.content;

    // Try to get from window.meta if available
    if (window.meta?.product?.id) return window.meta.product.id;

    return null;
  }

  loadStaticFallbackData() {
    this.items = [
      {
        id: 'sample1',
        image: '',
        title: 'Sample Product 1',
        price: '45.00',
        comparePrice: '55.00',
        description: 'This is a sample product description for the customizer view.',
        url: '#',
        nonClickable: false
      },
      {
        id: 'sample2',
        image: '',
        title: 'Sample Product 2',
        price: '35.00',
        comparePrice: '45.00',
        description: 'Another sample product description for the customizer view.',
        url: '#',
        nonClickable: false
      },
      {
        id: 'sample3',
        image: '',
        title: 'Sample Product 3',
        price: '25.00',
        comparePrice: '35.00',
        description: 'A third sample product description for the customizer view.',
        url: '#',
        nonClickable: false
      }
    ];
    this.render();
  }

  trimDescription(description) {
    return description.replace(/<[^>]*>?/g, '').split(/[.!?]/).slice(0, 1).join('.') + '.';
  }

  hasNonClickableTag(tags) {
    if (!tags) return false;

    if (Array.isArray(tags)) {
      return tags.some(tag => String(tag).toLowerCase() === 'non-clickable');
    }

    if (typeof tags === 'string') {
      return tags
        .split(',')
        .map(tag => tag.trim().toLowerCase())
        .includes('non-clickable');
    }

    return false;
  }

  async fetchMetaobject(gid) {
    try {
      const query = `
        query {
          metaobject(id: "${gid}") {
            handle
            type
            fields {
              key
              value
              type
              reference {
                ... on MediaImage {
                  image {
                    url
                  }
                }
              }
            }
          }
        }
      `;

      const response = await fetch('/api/2024-01/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': this.accessToken
        },
        body: JSON.stringify({ query })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.errors) {
        throw new Error(data.errors[0].message);
      }

      return data.data.metaobject;
    } catch (error) {
      console.error('Error fetching metaobject:', error);
      return null;
    }
  }

  async createProductItem(product) {
    const mainPrice = product.selected_variant.price;
    const compPrice = product.selected_variant.compare_at_price;
    // Trim the description to one sentence can be . or ! or ?	
    let trimedDescription = product.description.replace(/<[^>]*>?/g, '').split(/[.!?]/).slice(0, 1).join('.') + '.';

    let title = product.title;
    let url = product.link;
    let badge = null;
    let image = product.image.src;
    let description = product.metafields?.custom?.rebuy_short_description || product.metafields?.custom?.description || trimedDescription;

    // Check if we have a metaobject reference
    const metaobjectId = product.metafields?.custom?.override;
    if (metaobjectId) {
      const metaobject = await this.fetchMetaobject(metaobjectId);
      if (metaobject) {
        const fields = metaobject.fields.reduce((acc, field) => {
          if (field.key === 'image' && field.reference?.image?.url) {
            acc[field.key] = field.reference.image.url;
          } else {
            acc[field.key] = field.value;
          }
          return acc;
        }, {});

        title = fields.title || title;
        image = fields.image || image;
        badge = fields.badge;
      }
    }

    return {
      id: product.id,
      image: image,
      title: title,
      price: mainPrice,
      comparePrice: compPrice,
      description: description,
      badge: badge,
      url: url,
      nonClickable: this.hasNonClickableTag(product.tags)
    };
  }

  handleRebuyEvent(event) {
    const widgetEvent = event.detail || event;
    if (!this.isValidWidget(widgetEvent)) return;

    // Store the widget event for future reference
    window[`rebuy_widget_${this.blockId}`] = widgetEvent;
    this.items = [];

    // Convert to async/await pattern
    Promise.all(widgetEvent.widget.data.products.map(product =>
      this.createProductItem(product)
    ))
      .then(items => {
        this.items = items.filter(Boolean); // Filter out any null items
        // Cache the items for customizer refreshes
        window[`cachedUpsellItems_${this.blockId}`] = this.items;

        this.render();
      })
      .catch(error => {
        console.error('Error processing products:', error);
        if (this.skeleton) this.skeleton.style.display = 'none';
        if (this.container) this.container.style.display = 'block';
        if (this.productsContainer) {
          this.productsContainer.innerHTML = '<p>Failed to load products</p>';
        }
      });
  }

  isValidWidget(widgetEvent) {
    return widgetEvent?.widget?.data?.products &&
      widgetEvent.widget.id === this.widgetId;
  }

  calculateDiscount(price, comparePrice) {
    if (!comparePrice || comparePrice <= price) return null;

    const savings = comparePrice - price;
    const percentage = Math.round((savings / comparePrice) * 100);

    return {
      percentage,
      savings: savings.toFixed(2)
    };
  }

  render() {
    if (this.items.length === 0) {
      if (this.skeleton) this.skeleton.style.display = 'block';
      if (this.container) this.container.style.display = 'none';
      return;
    }

    if (this.skeleton) this.skeleton.style.display = 'none';
    if (this.container) this.container.style.display = 'block';

    if (this.productsContainer) {
      this.productsContainer.innerHTML = this.items
        .map((item, index) => {
          const discount = this.calculateDiscount(parseFloat(item.price), parseFloat(item.comparePrice));
          const productLink = !item.nonClickable && item.url ? item.url : null;
          const imageOverlayLink = productLink ? `<a href="${productLink}" class="product-upsell__url"></a>` : '';
          const contentOverlayLink = productLink ? `<a href="${productLink}" class="product-upsell__url"></a>` : '';
          const discountPill = discount ? `
            <span class="price-pill">
              <span class="igSavingsPercentage" data-product-id="${item.id}">${discount.percentage}% OFF</span> | Save <span class="igSavingsPrice" data-product-id="${item.id}">$${discount.savings}</span>
            </span>
          ` : '';

          return `
            <li class="product-upsell__item">
              <div class="fp-img product-upsell__image">
                ${imageOverlayLink}
                ${typeof item.image === 'string' ? `
                  <img
                    src="${item.image}"
                    width="100"
                    height="100"
                    loading="lazy"
                    alt="${item.title}"
                  >
                ` : item.image}
              </div>
              <div class="d-flex flex-column product-upsell__content">
                ${contentOverlayLink}
                <div class="d-flex align-items-center flex-wrap gap-2">
                  <span class="larger-text">
                  ${item.title} - <span class="igPrice" data-product-id="${item.id}">$${item.price}</span>
                  </span>
                  ${item.badge ? `<span class="badge price-pill">${item.badge}</span>` : `${discountPill}`}
                </div>
                <p class="msmall-text">${item.description.replace(/\.$/, '')}</p>
              </div>
              <button
                data-product-id="${item.id}"
                class="add-to-cart-button btn rounded d-none d-md-block"
                ${this.isCustomizer ? 'disabled' : ''}>
                + Add
              </button>
            </li>
            ${index < this.items.length - 1 ? '<div class="border-top"></div>' : ''}
          `;
        }).join('');

      if (!this.isCustomizer) {
        const buttons = this.productsContainer.querySelectorAll('.add-to-cart-button');
        buttons.forEach(button => {
          button.addEventListener('click', () => {
            const productId = button.getAttribute('data-product-id');
            this.addToCart(productId);
          });
        });
      }
    }
  }

  addToCart(productId) {
    const button = document.querySelector(`.product-id-${productId} .rebuy-button`);
    if (button) button.click();
  }

  // Public method to refresh the component
  refresh() {
    this.init();
  }
}

// Define the custom element
customElements.define('product-upsell', ProductUpsell);

// Auto-initialize when Rebuy events occur
document.addEventListener('rebuy.ready', () => {
  const upsellElements = document.querySelectorAll('product-upsell');
  upsellElements.forEach(element => element.refresh());
});

// Re-initialize on Shopify events
document.addEventListener('shopify:section:load', () => {
  const upsellElements = document.querySelectorAll('product-upsell');
  upsellElements.forEach(element => element.refresh());
});

document.addEventListener('shopify:section:select', () => {
  const upsellElements = document.querySelectorAll('product-upsell');
  upsellElements.forEach(element => element.refresh());
});

// Re-initialize if Rebuy loads after our script
window.addEventListener('rebuy:init', () => {
  const upsellElements = document.querySelectorAll('product-upsell');
  upsellElements.forEach(element => element.refresh());
});